import { cn } from '@utazzo/ui';
import { ChevronRight, CornerDownRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useMarketingLeadType } from '../lead-type';
import { useMarketingFieldVisibility } from '../use-marketing-reports';

import {
  GRAND_TOTAL_ROW,
  GRID_TABLE,
  numberTone,
  REPORT_HEAD_CELL,
  signedTone,
  useGroupOpen,
} from './month-table';

import type { LegacyCell } from '@/features/reports/components/ReportSection';
import type {
  ContactabilityRow,
  MarketingDestinationResponse,
  MarketingDestinationRow,
} from '@utazzo/shared';

import { LegacyTable, ReportSection } from '@/features/reports/components/ReportSection';

const inr = (amount: number): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('en-IN');
  }
};

const DASH = '-';
const count = (value: number): string => value.toLocaleString('en-IN');
const pct = (value: number | null): string => (value === null ? DASH : `${value.toFixed(2)}%`);
const wholePct = (value: number | null): string => (value === null ? DASH : `${value.toFixed(0)}%`);

/**
 * A nullable money figure. Null means the spend behind it was never captured -
 * rendered as a dash rather than ₹0, which would read as "we spent nothing".
 */
const money = (value: number | null): string => (value === null ? DASH : inr(value));

/** ROI / ROAS, reported as a multiple like the source sheet. */
const multiple = (value: number | null): string => (value === null ? DASH : `${value.toFixed(2)}x`);

/**
 * The pinned first column of the wide lost-reason pivot.
 *
 * A hard right edge plus a shadow, so where the frozen column ends and the
 * scrolling columns begin is unmistakable - without it a half-scrolled column
 * sitting against the pinned one reads as a broken, clipped column.
 */
const PINNED_COLUMN = 'border-border border-r-2 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.25)]';

/** Header band shared by every table in the report. */
const HEAD = REPORT_HEAD_CELL;

const ContactCells = ({
  row,
  muted,
  hideContacted,
}: {
  row: ContactabilityRow;
  muted?: boolean;
  hideContacted?: boolean;
}): React.JSX.Element => {
  const columns = [
    { text: count(row.queries), tone: numberTone(row.queries) },
    { text: count(row.lost), tone: numberTone(row.lost, 'bad') },
    ...(hideContacted
      ? []
      : [{ text: wholePct(row.contactedPct), tone: numberTone(row.contactedPct, 'good') }]),
  ];
  return (
    <>
      {columns.map((column, index) => (
        <td
          key={index}
          className={cn(
            'px-3 py-1.5 text-right whitespace-nowrap tabular-nums',
            muted ? 'text-muted-foreground' : column.tone,
          )}
        >
          {column.text}
        </td>
      ))}
    </>
  );
};

/**
 * "Destination Wise Contactibility" - channel, with the destinations booked
 * through it nested underneath. Destinations partition their channel exactly,
 * so a channel's children add up to it.
 */
export const DestinationContactability = ({
  data,
  loading,
}: {
  data: MarketingDestinationResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const groupOpen = useGroupOpen();
  const { isHidden } = useMarketingFieldVisibility();
  const hideContacted = isHidden('contactedPct');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <ReportSection title="Destination Wise Contactibility">
      {loading || !data ? (
        <div className="space-y-2 py-2">
          {[0, 1, 2].map((index) => (
            <div key={index} className="bg-muted h-8 animate-pulse rounded" />
          ))}
        </div>
      ) : data.contactability.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          No leads in the selected range.
        </p>
      ) : (
        <table className={cn('w-full min-w-md text-[15px]', GRID_TABLE)}>
          <thead>
            <tr>
              <th className={cn(HEAD, 'text-left')}>Name</th>
              <th className={cn(HEAD, 'text-right')}>Querys</th>
              <th className={cn(HEAD, 'text-right')}>Lost</th>
              {!hideContacted && <th className={cn(HEAD, 'text-right')}>Contacted %</th>}
            </tr>
          </thead>
          <tbody>
            {data.contactability.map((channel) => {
              const open = groupOpen(expanded.has(channel.key));
              return (
                <React.Fragment key={channel.key}>
                  <tr className="bg-muted/40 font-semibold">
                    <th
                      scope="row"
                      className="text-foreground px-3 py-1.5 text-left whitespace-nowrap"
                    >
                      <button
                        type="button"
                        onClick={() => toggle(channel.key)}
                        title={open ? 'Hide destinations' : 'Show destinations'}
                        className="flex items-center gap-1.5"
                      >
                        <ChevronRight
                          className={cn(
                            'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                            open && 'rotate-90',
                          )}
                        />
                        {channel.name}
                        <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                          {channel.destinations.length}
                        </span>
                      </button>
                    </th>
                    <ContactCells row={channel} hideContacted={hideContacted} />
                  </tr>
                  {open &&
                    channel.destinations.map((destination) => (
                      <tr key={`${channel.key}-${destination.key}`}>
                        <th
                          scope="row"
                          className="text-muted-foreground px-3 py-1.5 text-left font-normal whitespace-nowrap"
                        >
                          <span className="flex items-center gap-1.5 pl-5">
                            <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
                            {destination.name}
                          </span>
                        </th>
                        <ContactCells row={destination} muted hideContacted={hideContacted} />
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
            <tr className={GRAND_TOTAL_ROW}>
              <th scope="row" className="text-foreground px-3 py-2 text-left whitespace-nowrap">
                {data.contactabilityTotal.name}
              </th>
              <ContactCells row={data.contactabilityTotal} hideContacted={hideContacted} />
            </tr>
          </tbody>
        </table>
      )}
    </ReportSection>
  );
};

/**
 * "Destination wise Lost Region" - LOST leads only, channel with its
 * destinations nested, against the reason each lead was lost for.
 *
 * Reason columns are discovered from the data and ordered by volume, so the
 * reasons actually costing leads sit leftmost. Wide by nature, so the table
 * scrolls horizontally with the label column pinned.
 */
export const DestinationLostReasons = ({
  data,
  loading,
}: {
  data: MarketingDestinationResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const groupOpen = useGroupOpen();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const pivot = data?.lostReasons;

  return (
    <ReportSection title="Destination wise Lost Region">
      {loading || !pivot ? (
        <div className="space-y-2 py-2">
          {[0, 1, 2].map((index) => (
            <div key={index} className="bg-muted h-8 animate-pulse rounded" />
          ))}
        </div>
      ) : pivot.channels.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          No lost leads in the selected range.
        </p>
      ) : (
        <table className={cn('w-full min-w-lg text-[15px]', GRID_TABLE)}>
          <thead>
            <tr>
              <th className={cn(HEAD, PINNED_COLUMN, 'sticky left-0 z-40 text-left')}>
                Row Labels
              </th>
              {pivot.columns.map((column) => (
                <th key={column} className={cn(HEAD, 'text-right')}>
                  {column}
                </th>
              ))}
              <th className={cn(HEAD, 'text-right')}>Grand Total</th>
            </tr>
          </thead>
          <tbody>
            {pivot.channels.map((channel) => {
              const open = groupOpen(expanded.has(channel.key));
              return (
                <React.Fragment key={channel.key}>
                  <tr className="bg-muted/40 font-semibold">
                    <th
                      scope="row"
                      className={cn(
                        'bg-muted text-foreground sticky left-0 z-20 px-3 py-1.5 text-left whitespace-nowrap',
                        PINNED_COLUMN,
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggle(channel.key)}
                        title={open ? 'Hide destinations' : 'Show destinations'}
                        className="flex items-center gap-1.5"
                      >
                        <ChevronRight
                          className={cn(
                            'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                            open && 'rotate-90',
                          )}
                        />
                        {channel.name}
                      </button>
                    </th>
                    {channel.cells.map((value, index) => (
                      <td
                        key={index}
                        className={cn(
                          'px-3 py-1.5 text-right whitespace-nowrap tabular-nums',
                          numberTone(value),
                        )}
                      >
                        {count(value)}
                      </td>
                    ))}
                    <td className="px-3 py-1.5 text-right whitespace-nowrap tabular-nums">
                      {count(channel.total)}
                    </td>
                  </tr>
                  {open &&
                    channel.destinations.map((destination) => (
                      <tr key={`${channel.key}-${destination.key}`}>
                        <th
                          scope="row"
                          className={cn(
                            'bg-card text-muted-foreground sticky left-0 z-20 px-3 py-1.5 text-left font-normal whitespace-nowrap',
                            PINNED_COLUMN,
                          )}
                        >
                          <span className="flex items-center gap-1.5 pl-5">
                            <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
                            {destination.name}
                          </span>
                        </th>
                        {destination.cells.map((value, index) => (
                          <td
                            key={index}
                            className="text-muted-foreground px-3 py-1.5 text-right whitespace-nowrap tabular-nums"
                          >
                            {count(value)}
                          </td>
                        ))}
                        <td className="text-foreground px-3 py-1.5 text-right font-medium whitespace-nowrap tabular-nums">
                          {count(destination.total)}
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
            <tr className={GRAND_TOTAL_ROW}>
              <th
                scope="row"
                className={cn(
                  'bg-muted text-foreground sticky left-0 z-20 px-3 py-2 text-left whitespace-nowrap',
                  PINNED_COLUMN,
                )}
              >
                Grand Total
              </th>
              {pivot.total.cells.map((value, index) => (
                <td key={index} className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {count(value)}
                </td>
              ))}
              <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                {count(pivot.total.total)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </ReportSection>
  );
};

/**
 * Destination x "How soon will customer book?".
 *
 * The bucket shares sit as a "% of Total" row directly under Grand Total, in
 * the same columns - a separate summary block underneath forced the reader to
 * re-map three numbers onto three columns by eye.
 *
 * Only leads that answered the field appear, which is why the Grand Total here
 * is smaller than the destination query volume above; the unanswered count is
 * called out so the gap is explained rather than mysterious.
 */
export const DestinationTimeline = ({
  data,
  loading,
}: {
  data: MarketingDestinationResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const totals = data?.timelineTotal;
  const rows = data?.timeline ?? [];

  return (
    <ReportSection title="Destination Wise Booking Timeline">
      {loading || !data ? (
        <div className="space-y-2 py-2">
          {[0, 1, 2].map((index) => (
            <div key={index} className="bg-muted h-8 animate-pulse rounded" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          No leads answered &ldquo;How soon will customer book?&rdquo; in this range.
        </p>
      ) : (
        <>
          <table className={cn('w-full min-w-md text-[15px]', GRID_TABLE)}>
            <thead>
              <tr>
                <th className={cn(HEAD, 'text-left')}>Destination</th>
                {['0-30 Days', '30-60 Days', '60+ Days', 'Grand Total'].map((label) => (
                  <th key={label} className={cn(HEAD, 'text-right')}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <th
                    scope="row"
                    className="text-foreground px-3 py-1.5 text-left font-normal whitespace-nowrap"
                  >
                    {row.name}
                  </th>
                  {[row.within30Days, row.thirtyToSixtyDays, row.overSixtyDays, row.total].map(
                    (value, index) => (
                      <td
                        key={index}
                        className={cn(
                          'px-3 py-1.5 text-right whitespace-nowrap tabular-nums',
                          numberTone(value),
                        )}
                      >
                        {count(value)}
                      </td>
                    ),
                  )}
                </tr>
              ))}
              {totals && (
                <>
                  <tr className={GRAND_TOTAL_ROW}>
                    <th
                      scope="row"
                      className="text-foreground px-3 py-2 text-left whitespace-nowrap"
                    >
                      Grand Total
                    </th>
                    {[
                      totals.within30Days.count,
                      totals.thirtyToSixtyDays.count,
                      totals.overSixtyDays.count,
                      totals.specified,
                    ].map((value, index) => (
                      <td
                        key={index}
                        className="px-3 py-2 text-right whitespace-nowrap tabular-nums"
                      >
                        {count(value)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th
                      scope="row"
                      className="text-muted-foreground px-3 py-1.5 text-left font-normal whitespace-nowrap"
                    >
                      % of Total
                    </th>
                    {[
                      totals.within30Days.pct,
                      totals.thirtyToSixtyDays.pct,
                      totals.overSixtyDays.pct,
                    ].map((value, index) => (
                      <td
                        key={index}
                        className="text-foreground px-3 py-1.5 text-right font-semibold whitespace-nowrap tabular-nums"
                      >
                        {wholePct(value)}
                      </td>
                    ))}
                    <td className="text-muted-foreground px-3 py-1.5 text-right whitespace-nowrap tabular-nums">
                      {totals.specified > 0 ? '100%' : DASH}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          {totals && totals.unspecified > 0 && (
            <p className="text-muted-foreground mt-3 text-xs">
              {count(totals.unspecified)} lead(s) left the field blank and are excluded from these
              counts and shares.
            </p>
          )}
        </>
      )}
    </ReportSection>
  );
};

/**
 * A figure with a colour, for a sortable table.
 *
 * `sort` carries the FORMATTED text rather than the raw number, which is what
 * the plain cells passed before this wrapper existed: the sorter already reads
 * "₹1,23,456" and "12.3%" as numbers, so column order is untouched by the tint.
 */
const toned = (text: string, tone: string): LegacyCell => ({
  node: <span className={tone}>{text}</span>,
  sort: text,
});

/**
 * The profitability columns measured against spend - Accounts Spend, Cost Per
 * Query, Profitability - Market spend, ROI, ROAS. The Organic section has no
 * cost side, so it drops these (by index, in `profitabilityRow` order).
 */
const SPEND_COLUMNS = new Set([2, 3, 7, 9, 10]);

/**
 * Catalog key per profitability column, index-aligned with PROFITABILITY_HEADERS
 * / profitabilityRow. `null` = a structural column with no hideable key. Used to
 * drop columns the admin hid from this user.
 */
const PROFITABILITY_COLUMN_KEYS: (string | null)[] = [
  null, // Destination
  null, // Total Lead
  'spend', // Accounts Spend
  'costPerQuery', // Cost Per Query
  null, // Won
  'conversionPct', // Convertion %
  'salesProfit', // Profitability
  'profitAfterSpend', // Profitability - Market spend
  'ttvAmount', // TTV
  'roi', // ROI
  'roas', // ROAS
];

const PROFITABILITY_HEADERS = [
  'Destination',
  'Total Lead',
  'Accounts Spend',
  'Cost Per Query',
  'Won',
  'Convertion %',
  'Profitability',
  'Profitability - Market spend',
  'TTV',
  'ROI',
  'ROAS',
];

/** One row of the profitability table, coloured by what each column means. */
const profitabilityRow = (row: MarketingDestinationRow): LegacyCell[] => [
  row.name,
  toned(String(row.overall.query), numberTone(row.overall.query)),
  money(row.spend),
  money(row.costPerQuery),
  toned(String(row.overall.converted), numberTone(row.overall.converted, 'good')),
  toned(pct(row.overall.conversionPct), numberTone(row.overall.conversionPct, 'good')),
  toned(money(row.salesProfit), signedTone(row.salesProfit ?? 0)),
  toned(money(row.profitAfterSpend), signedTone(row.profitAfterSpend)),
  money(row.overall.ttvAmount),
  toned(multiple(row.roi), signedTone(row.roi)),
  toned(multiple(row.roas), signedTone(row.roas)),
];

/**
 * "Destination Wise Profitability" over the selected range.
 *
 * Single-period rather than month-by-month: the destination list is long, so a
 * month matrix would be unreadable. Built on `LegacyTable` so every column is
 * click-to-sort - useful when hunting for the best margin rather than volume.
 *
 * The five spend-derived columns (Accounts Spend, Cost Per Query, Profitability
 * - Market spend, ROI, ROAS) used to render as em dashes: ad spend existed only
 * as ONE monthly total, and the source sheet's per-destination figures are
 * demonstrably not lead-proportional (Bali 329 leads / 13,763 spend vs Vietnam
 * 104 leads / 13,802), so allocating that total pro-rata would have made Cost
 * Per Query identical for every destination - visibly wrong.
 *
 * They are real now. Spend is captured per ADSET, and an adset targets exactly
 * one destination, so the figure here is measured rather than allocated. It
 * still falls back to em dashes when no adset spend has been entered for the
 * range at all - "not captured" and "spent nothing" are different answers.
 */
export const DestinationProfitability = ({
  data,
  loading,
}: {
  data: MarketingDestinationResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const costless = useMarketingLeadType() === 'organic';
  const { isHidden } = useMarketingFieldVisibility();
  // Columns to drop: the per-user hidden ones, plus (for organic) the spend set.
  const dropIdx = useMemo(() => {
    const set = new Set<number>();
    PROFITABILITY_COLUMN_KEYS.forEach((key, index) => {
      if (key && isHidden(key)) set.add(index);
    });
    if (costless) for (const index of SPEND_COLUMNS) set.add(index);
    return set;
  }, [isHidden, costless]);
  const keep = <T,>(arr: T[]): T[] => arr.filter((_, index) => !dropIdx.has(index));
  const cells = (row: MarketingDestinationRow): LegacyCell[] => keep(profitabilityRow(row));
  return (
    <ReportSection title="Destination Wise Profitability">
      <LegacyTable
        // Matches the other tables in this report, which run a step larger than
        // the app default so a column of counts stays legible.
        className="text-[15px]"
        loading={loading || !data}
        emptyText="No leads in the selected range."
        headers={keep(PROFITABILITY_HEADERS)}
        rows={(data?.destinations ?? []).map((destination) => cells(destination))}
        totals={data && cells(data.total)}
      />
      {!loading && data && (
        <>
          <p className="text-muted-foreground mt-3 text-xs">
            Total Lead counts leads created in the range; Won counts leads won in it, so a lead
            created earlier and won now counts here. Profitability is the gross markup booked, TTV
            the sale value. &ldquo;Not specified&rdquo; is leads with no destination picked. Click
            any column to sort.
          </p>
          {!costless && (
            <p className="text-muted-foreground mt-1 text-xs">
              {data.total.spend === null
                ? 'Accounts Spend, Cost Per Query, Profitability - Market spend, ROI and ROAS stay blank until adset spend is entered on the Overview tab. They are never split from a pooled total by a guess.'
                : 'Accounts Spend is measured, not allocated: it is the spend entered against the adsets that sold each destination, and each adset targets one destination. ROI is profit after spend over spend; ROAS is profit over spend.'}
            </p>
          )}
        </>
      )}
    </ReportSection>
  );
};

/**
 * Every destination report, stacked - what the Destinations tab and the
 * Overview show. The workbook uses the four pieces separately, one per sheet.
 */
export const DestinationsTable = ({
  data,
  loading,
}: {
  data: MarketingDestinationResponse | undefined;
  loading: boolean;
}): React.JSX.Element => (
  <>
    <DestinationProfitability data={data} loading={loading} />
    <DestinationContactability data={data} loading={loading} />
    <DestinationLostReasons data={data} loading={loading} />
    <DestinationTimeline data={data} loading={loading} />
  </>
);
